import React from 'react'
import { format } from '@e3dc-react/shell/Libs/date'
import { Trans } from 'react-i18next'
import ErrorBanner from './ErrorBanner'
import { Typography } from '@mui/material'
import { Installation } from 'src/Libs/installations/models/installation.model'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useInstallationHistory } from 'src/Libs/installations/hooks/use-installation-history'

interface InstallationDecomissionedBannerProps {
  installation: Installation | null
}

/**
 * Displays a warning banner if a service-user is looking at a decomissioned installation
 * @constructor
 * @param installation
 */
const InstallationDecommissionedBanner: React.FC<InstallationDecomissionedBannerProps> = ({ installation }) => {
  const { t } = useCustomTranslation()
  const [{ data: installationHistoryData, hasError }] = useInstallationHistory(installation?.id.toString() ?? '')
  const historyEntry = installationHistoryData?.find(entry => {
    return entry.deviceLinkId === null && entry.action === 'REMOVED'
  })

  if (hasError) {
    return (
      <ErrorBanner visible={!!installation?.decommissionedAt}>
        <Typography>{t('installation-details.deleted.error')}</Typography>
      </ErrorBanner>
    )
  }

  const decommissionedBy = historyEntry?.userName ?? t('installation-details.deleted.unknown-user')
  const decommissioningReason = historyEntry?.reason ?? t('installation-details.deleted.unknown-reason')

  return (
    <ErrorBanner visible={!!installation?.decommissionedAt}>
      <Trans
        i18nKey="installation-details.deleted.message"
        components={{
          deletedAt: <Typography component="strong" sx={{ fontWeight: 'bold' }} />,
          deletedBy: <Typography component="strong" sx={{ fontWeight: 'bold' }} />,
        }}
        values={{
          deletedAt: installation?.decommissionedAt && format(new Date(installation.decommissionedAt), t('common.format.date-fns-date')),
          deletedBy: decommissionedBy,
        }}
      />
      <Typography sx={{ fontWeight: 'bold' }}>{decommissioningReason}</Typography>
    </ErrorBanner>
  )
}

export default InstallationDecommissionedBanner
